import {
  getCoreRowModel,
  useReactTable,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type TableOptions,
  type VisibilityState,
} from "@tanstack/react-table";
import { useLocalStorage } from "react-use";

export function useDataTable<TData extends { id: string }>(
  options: Omit<TableOptions<TData>, "getCoreRowModel"> & Partial<TableOptions<TData>> & { name: string },
) {
  const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
    `sizing/${options.name}`,
    options.initialState?.columnSizing ?? {},
  );
  const [columnPinning, setColumnPinning] = useLocalStorage<ColumnPinningState>(
    `pinning/${options.name}`,
    options.initialState?.columnPinning ?? {},
  );
  const [savedColumnVisibility, setSavedColumnVisibility] = useLocalStorage<VisibilityState>(
    `visibility/${options.name}`,
    options.initialState?.columnVisibility ?? {},
  );
  const [columnOrder, setColumnOrder] = useLocalStorage<ColumnOrderState>(
    `order/${options.name}`,
    options.initialState?.columnOrder ?? [],
  );

  // Ensure hidden columns are always hidden, even if saved state says otherwise
  const columnVisibility = {
    ...savedColumnVisibility,
    ...Object.fromEntries(
      options.columns
        .filter((col) => "id" in col && col.id && col.meta?.hiddenColumn)
        .map((col) => ["id" in col && col.id ? col.id : "", false]),
    ),
  };

  const setColumnVisibility = (value: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
    const newState = typeof value === "function" ? value(columnVisibility) : value;
    setSavedColumnVisibility(newState);
  };

  // Ensure columns with stickToLeft meta are always first in the order
  const ensureStickToLeftColumns = (order: ColumnOrderState): ColumnOrderState => {
    if (!Array.isArray(order) || order.length === 0) return order;

    const stickToLeftColumns = options.columns
      .filter((col) => col.meta?.stickToLeft === true)
      .map((col) => col.id ?? "")
      .filter(Boolean);

    if (stickToLeftColumns.length === 0) return order;

    // Remove stick-to-left columns from current order and prepend them
    const otherColumns = order.filter((id) => !stickToLeftColumns.includes(id));
    return [...stickToLeftColumns, ...otherColumns];
  };

  const safeColumnOrder = Array.isArray(columnOrder) ? columnOrder : [];
  const finalColumnOrder = ensureStickToLeftColumns(safeColumnOrder);

  // Ensure stickToLeft columns are always pinned to the left and at the front
  const ensureStickToLeftPinning = (pinning: ColumnPinningState): ColumnPinningState => {
    const stickToLeftColumns = options.columns
      .filter((col) => col.meta?.stickToLeft === true)
      .map((col) => col.id ?? "")
      .filter(Boolean);

    if (stickToLeftColumns.length === 0) return pinning;

    // Add stick-to-left columns to left pinning if not already there
    const currentLeft = pinning.left ?? [];
    const otherLeftPinnedColumns = currentLeft.filter((id) => !stickToLeftColumns.includes(id));

    return {
      ...pinning,
      left: [...stickToLeftColumns, ...otherLeftPinnedColumns],
    };
  };

  const safeColumnPinning = columnPinning ?? {};
  const finalColumnPinning = ensureStickToLeftPinning(safeColumnPinning);
  const safeColumnSizing = columnSizing ?? {};

  const table = useReactTable({
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    enableColumnResizing: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    columnResizeMode: "onChange",
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: (value) => {
      const newState = typeof value === "function" ? value(columnVisibility) : value;
      setColumnVisibility(newState);
    },
    onColumnPinningChange: (value) => {
      const newState = typeof value === "function" ? value(finalColumnPinning) : value;
      const finalNewState = ensureStickToLeftPinning(newState);
      setColumnPinning(finalNewState);
    },
    onColumnSizingChange: (value) => {
      const newState = typeof value === "function" ? value(safeColumnSizing) : value;
      setColumnSizing(newState);
    },
    onColumnOrderChange: (value) => {
      const newState = typeof value === "function" ? value(finalColumnOrder) : value;
      const finalNewState = ensureStickToLeftColumns(newState);
      setColumnOrder(finalNewState);
    },
    ...options,
    defaultColumn: {
      minSize: 120,
      maxSize: 500,
      enableGlobalFilter: false,
      enableColumnFilter: false,
      ...options.defaultColumn,
    },
    state: {
      columnSizing: safeColumnSizing,
      columnPinning: finalColumnPinning,
      columnVisibility,
      columnOrder: finalColumnOrder,
      ...(options.state ?? {}),
    },
  });

  return table;
}
